import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

interface Preset {
  id: string;
  name: string;
  emoji: string;
  shortDescription: string;
}

const CompanionPreset: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // If personality text is >= 500 chars, skip this step
    const personality = sessionStorage.getItem('companionPersonality') || '';
    if (personality.length >= 500) {
      sessionStorage.setItem('companionSkipPreset', 'true');
      navigate('/companion/avatar');
      return;
    }

    axios
      .get('/companions/presets')
      .then((res) => setPresets(res.data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [navigate]);

  const handleContinue = () => {
    if (selectedPreset) {
      sessionStorage.setItem('companionPresetId', selectedPreset);
    }
    navigate('/companion/avatar');
  };

  const handleSkip = () => {
    // Random assignment happens server-side when no presetId is sent
    sessionStorage.removeItem('companionPresetId');
    navigate('/companion/avatar');
  };

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <div className="pt-safe px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-dark-surface dark:hover:bg-dark-surface hover:bg-light-surface"
          >
            <ArrowLeftIcon className="w-5 h-5 text-dark-text dark:text-dark-text text-light-text" />
          </button>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Progress indicator - 6 steps, step 5 active */}
          <div className="flex space-x-2 mb-8">
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-primary-500 rounded-full" />
            <div className="h-1 flex-1 bg-light-surface dark:bg-dark-surface rounded-full" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text mb-2">
            {t('companionCreation.presetTitle', 'Choose a personality style')}
          </h1>
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary mb-6">
            {t(
              'companionCreation.presetSubtitle',
              'Give your companion a rich personality foundation. You can always customize later.',
            )}
          </p>

          {/* Preset Grid */}
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-dark-text-secondary">{t('common.loading', 'Loading...')}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 mb-auto overflow-y-auto">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => setSelectedPreset(selectedPreset === preset.id ? null : preset.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    selectedPreset === preset.id
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-light-border dark:border-dark-border hover:border-primary-500/50 bg-light-surface dark:bg-dark-card'
                  }`}
                >
                  <div className="text-3xl mb-2">{preset.emoji}</div>
                  <div className="font-semibold text-sm text-light-text dark:text-dark-text mb-1">
                    {t(`companionCreation.preset.${preset.id}.name`, preset.name)}
                  </div>
                  <div className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-tight">
                    {t(
                      `companionCreation.preset.${preset.id}.description`,
                      preset.shortDescription,
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-3 mt-6">
            <button onClick={handleContinue} className="btn-primary">
              {selectedPreset
                ? t('common.continue', 'Continue')
                : t('companionCreation.presetSkip', 'Continue without choosing')}
            </button>
            <button onClick={handleSkip} className="btn-secondary">
              {t('companionCreation.skipForNow', 'Skip for now')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionPreset;
